'use client';

import {
  Button,
  ButtonGroup,
} from '@ironhack/design-system2/components/button';
import { Checkbox } from '@ironhack/design-system2/components/checkbox';
import { Flex } from '@ironhack/design-system2/components/flex';
import {
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
} from '@ironhack/design-system2/components/popover';
import { Text } from '@ironhack/design-system2/components/text';
import { useDisclosure } from '@ironhack/design-system2/hooks';
import { deleteCookie, getCookie } from 'cookies-next';
import { useRouter } from 'next/navigation';
import { type ReactElement, type ReactNode, useEffect, useState } from 'react';
import { useSessionStorage, useToggle } from 'react-use';

import { type DatoGeoRedirectSuggestion } from '@/app/components/server/navbar/types';
import { type PageType, getPageUrl } from '@/app/urls/url-mapping';
import { usePageContext } from '@/contexts/page-context/use-page-context';
import { commonKeys, countryToRegion } from '@/lib/utils';
import { regionMainLanguage, regionToLanguages } from '@/lib/utils/language';
import { urlToDatoLocale } from '@/lib/utils/locale';
import { type GeoRedirectCookie, type RegionCountry } from '@/types';

type Props = {
  texts: DatoGeoRedirectSuggestion;
  page: PageType;
  children: ReactNode;
};

export const RegionSuggestion = (props: Props): ReactElement => {
  const { texts, page, children } = props;

  const router = useRouter();
  const {
    params: { language, region: currentRegion },
  } = usePageContext();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [geoRedirectCookie, setGeoRedirectCookie] =
    useState<GeoRedirectCookie>();
  const [rememberSelection, toggleRememberSelection] = useToggle(true);
  const [isActioned, setIsActioned] = useState(false);
  const [sessionSelection, setSessionSelection] = useSessionStorage(
    'geo_redirect_preference',
    ''
  );

  useEffect(() => {
    if (geoRedirectCookie) return;

    const rawGeoRedirectCookie = getCookie('geo_redirect');
    if (typeof rawGeoRedirectCookie === 'string') {
      try {
        const parsedCookie = JSON.parse(
          rawGeoRedirectCookie
        ) as GeoRedirectCookie;

        const cloudflareCountry = getCookie('ss_country_code');
        if (typeof cloudflareCountry === 'string') {
          const cloudflareRegion =
            countryToRegion[cloudflareCountry.toLowerCase() as RegionCountry];
          if (cloudflareRegion) parsedCookie.suggested = cloudflareRegion;
        }
        setGeoRedirectCookie(parsedCookie);
      } catch {
        // If the cookie cannot be parsed for any reason, delete it
        deleteCookie('geo_redirect');
      }
    }
  }, [geoRedirectCookie]);

  // Return children for early return so logo still appears
  if (!geoRedirectCookie) return <>{children}</>;

  // Derive if the popover should be opened or closed based on the current state
  const showPopover =
    !isActioned && // Popover has not been actioned by the user
    !sessionSelection && // There is no preference set for current session
    geoRedirectCookie && // The cookie is present
    !geoRedirectCookie.preference && // There is no region preference set in the cookie
    currentRegion !== geoRedirectCookie.suggested; // The current and suggested regions are different
  const openPopover = showPopover && !isOpen;
  const closePopover = !showPopover && isOpen;
  if (openPopover) onOpen();
  if (closePopover) onClose();

  const handleSelection = (useSuggestion: boolean) => {
    if (!geoRedirectCookie) return;

    const targetRegion = useSuggestion
      ? geoRedirectCookie.suggested
      : currentRegion;
    const updatedCookie: GeoRedirectCookie = {
      ...geoRedirectCookie,
      ...(rememberSelection && { preference: targetRegion }),
    };

    // If the checkbox is ticked, store the selection in the cookie.
    // If not, store the selection for the duration of the session.
    if (rememberSelection) {
      // eslint-disable-next-line unicorn/no-document-cookie -- js-cookie's `setCookie` is not used here as it escapes the json object characters
      document.cookie = `geo_redirect=${JSON.stringify(updatedCookie)}; path=/`;
    } else {
      setSessionSelection(targetRegion);
    }
    if (useSuggestion) {
      const suggestedPath =
        getPageUrl(page, { region: targetRegion, language }) ??
        getPageUrl(page, {
          region: targetRegion,
          language: regionMainLanguage[targetRegion],
        }) ??
        getPageUrl('home', {
          region: targetRegion,
          language: regionMainLanguage[targetRegion],
        });
      router.push(suggestedPath as string);
    }
    setGeoRedirectCookie(updatedCookie);
    setIsActioned(true);
  };

  const targetLanguage = regionToLanguages[geoRedirectCookie.suggested][0];
  const currentRegionName =
    commonKeys[targetLanguage][`region_${currentRegion}`];
  const suggestedRegionName =
    commonKeys[targetLanguage][`region_${geoRedirectCookie.suggested}`];
  const datoLocale =
    urlToDatoLocale[`${geoRedirectCookie.suggested}${targetLanguage}`];
  const message =
    texts._allMessageLocales.find(({ locale }) => locale === datoLocale)
      ?.value || '';
  const remember =
    texts._allRememberSelectionLocales.find(
      ({ locale }) => locale === datoLocale
    )?.value || '';

  return (
    <Popover
      arrowSize={16}
      gutter={16}
      isLazy
      isOpen={isOpen}
      onClose={() => handleSelection(false)}
      placement="bottom-start"
    >
      <PopoverTrigger>{children}</PopoverTrigger>
      <PopoverContent
        bg="fill.white"
        borderRadius="lg"
        color="text.primary"
        p={2}
        w="328px"
      >
        <PopoverArrow bg="fill.white" borderRadius={4} />
        <PopoverCloseButton color="fill.medium" size="md" top={1.5} />
        <PopoverBody as={Flex} flexDirection="column" gap={2} p={0}>
          <Text pr={4} textStyle="mBold">
            {message.replace('{region}', suggestedRegionName)}
          </Text>
          <Checkbox
            isChecked={rememberSelection}
            onChange={toggleRememberSelection}
          >
            <Text textStyle="s">{remember}</Text>
          </Checkbox>
          <ButtonGroup as={Flex} flexWrap="wrap" gap={1}>
            <Button
              flexGrow={1}
              onClick={() => handleSelection(false)}
              size="s"
              variant="secondary"
            >
              {currentRegionName}
            </Button>
            <Button
              flexGrow={1}
              onClick={() => handleSelection(true)}
              size="s"
              style={{ marginInlineStart: 0 }} // This is needed to averride default ButtonGroup styles
            >
              {suggestedRegionName}
            </Button>
          </ButtonGroup>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
